import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Technique } from '@/types/technique';
import { TechniqueCard } from '@/components/TechniqueCard';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { TechniquesPerPageSelector } from './TechniquesPerPageSelector';

interface TechniqueListProps {
  techniques: Technique[];
  onSaveTechnique: (techniqueId: string, e: React.MouseEvent) => void;
  savedTechniqueIds: string[];
  likedTechniqueIds?: string[];
  hiddenTechniqueIds?: string[];
  techniqueLikeCounts?: Record<string, number>;
  onLikeToggle?: (techniqueId: string, e: React.MouseEvent) => void;
  onHideToggle?: (techniqueId: string, e: React.MouseEvent) => void;
  isLoading?: boolean;
  itemsPerPage?: number;
  onItemsPerPageChange?: (value: number) => void;
}

export const TechniqueList = ({
  techniques,
  onSaveTechnique,
  savedTechniqueIds,
  likedTechniqueIds = [],
  hiddenTechniqueIds = [],
  techniqueLikeCounts = {},
  onLikeToggle,
  onHideToggle,
  isLoading = false,
  itemsPerPage = 9,
  onItemsPerPageChange
}: TechniqueListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);
  
  const effectiveItemsPerPage = itemsPerPage;
  
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {[...Array(isMobile ? 2 : 6)].map((_, index) => (
          <div
            key={index}
            className="h-60 md:h-72 lg:h-80 bg-muted/40 animate-pulse rounded-xl"
          />
        ))}
      </div>
    );
  }

  if (techniques.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 md:py-12 text-center">
        <div className="bg-primary/10 p-3 rounded-full mb-4">
          <svg
            className="h-5 w-5 md:h-6 md:w-6 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-base md:text-lg font-medium mb-2">No techniques found</h3>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          We couldn't find any techniques matching your criteria. Try adjusting your filters or search terms.
        </p>
      </div>
    );
  }

  const showAllTechniques = effectiveItemsPerPage >= 1000;
  
  const totalPages = showAllTechniques ? 1 : Math.ceil(techniques.length / effectiveItemsPerPage);
  const startIndex = showAllTechniques ? 0 : (currentPage - 1) * effectiveItemsPerPage;
  const paginatedTechniques = showAllTechniques 
    ? techniques 
    : techniques.slice(startIndex, startIndex + effectiveItemsPerPage);

  const goToNextPage = () => {
    setCurrentPage((prev: number) => Math.min(prev + 1, totalPages));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goToPrevPage = () => {
    setCurrentPage((prev: number) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveTechnique = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSaveTechnique(id, e);
  };

  const handleLikeToggle = (id: string, e: React.MouseEvent) => {
    if (onLikeToggle) {
      e.preventDefault();
      e.stopPropagation();
      onLikeToggle(id, e);
    }
  };

  const handleHideToggle = (id: string, e: React.MouseEvent) => {
    if (onHideToggle) {
      e.preventDefault();
      e.stopPropagation();
      onHideToggle(id, e);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6 w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {paginatedTechniques.map((technique) => (
          <Link to={`/techniques/${technique.slug}`} key={technique.id}>
            <TechniqueCard
              id={technique.id}
              name={technique.name}
              description={technique.description}
              level={technique.difficulty}
              type={technique.technique_type}
              position={technique.position?.name || ''}
              imageUrl={technique.image_url || undefined}
              onSave={(e) => handleSaveTechnique(technique.id, e)}
              saved={savedTechniqueIds.includes(technique.id)}
              isUserCreated={technique.is_user_created}
              isLiked={likedTechniqueIds.includes(technique.id)}
              isHidden={hiddenTechniqueIds.includes(technique.id)}
              likeCount={techniqueLikeCounts[technique.id] || 0}
              onLikeToggle={(e) => handleLikeToggle(technique.id, e)}
              onHideToggle={technique.is_user_created ? (e) => handleHideToggle(technique.id, e) : undefined}
            />
          </Link>
        ))}
      </div>

      <div className="flex flex-col items-center gap-3">
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 md:gap-2">
            <Button 
              variant="outline" 
              size={isMobile ? "sm" : "default"}
              onClick={goToPrevPage} 
              disabled={currentPage === 1}
              className="text-xs md:text-sm py-1 md:py-2 px-2 md:px-3"
            >
              <ChevronLeft className="h-3 w-3 md:h-4 md:w-4 mr-1" />
              {!isMobile && "Previous"}
            </Button>
            
            <span className="text-xs md:text-sm mx-2">
              {currentPage} / {totalPages}
            </span>
            
            <Button 
              variant="outline" 
              size={isMobile ? "sm" : "default"}
              onClick={goToNextPage} 
              disabled={currentPage === totalPages}
              className="text-xs md:text-sm py-1 md:py-2 px-2 md:px-3"
            >
              {!isMobile && "Next"}
              <ChevronRight className="h-3 w-3 md:h-4 md:w-4 ml-1" />
            </Button>
          </div>
        )}
        
        {onItemsPerPageChange && (
          <TechniquesPerPageSelector
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        )}
      </div>
    </div>
  );
};
